/* eslint-disable no-extra-boolean-cast */
import React, { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import {
  CustomProfile,
  OnboardingPopup,
  OnboardingPopupChoice,
} from '@customTypes/chapter';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import {
  FloatingPrevBtn,
  FloatingPrevDisableBtn,
  FloatingNextBtn,
  FloatingNextDisableBtn,
} from 'public/assets';
import throttle from 'lodash/throttle';
import SPImage from '@components/commons/image/SPImage';
import { getSignedNumberString } from '@common/utils';
import Button from '@components/commons/button/Button';

const ChoiceItem = ({
  isChecked = false,
  onCheck,
  popupChoiceProp,
  isProfile = false,
}: {
  popupChoiceProp: OnboardingPopupChoice;
  isChecked?: boolean;
  onCheck: ({
    popupChoiceId,
    props,
    imageFileId,
    imageFile,
    playerClassId,
  }: {
    popupChoiceId: number;
    props?: Record<string, number | string>;
    imageFileId?: number;
    imageFile?: {
      link: string;
    };
    playerClassId?: number;
  }) => void;
  isProfile?: boolean;
}) => {
  const content = popupChoiceProp?.content;
  const image = popupChoiceProp.imageFile?.link;
  const choiceProps:
    | Record<'display' | 'effect', Record<string, number | string>>
    | undefined = popupChoiceProp?.choiceProps;

  const displays = choiceProps?.['display'];
  const effects = choiceProps?.['effect'];
  const keys = displays ? Object.keys(displays) : undefined;

  return (
    <div
      css={[choiceProp, isChecked && selected]}
      onClick={() => {
        popupChoiceProp.status === 'Active' &&
          onCheck({
            popupChoiceId: popupChoiceProp.popupChoiceId,
            props: effects,
            imageFileId: popupChoiceProp?.imageFileId,
            imageFile: popupChoiceProp?.imageFile,
            playerClassId: popupChoiceProp?.playerClassId,
          });
      }}
    >
      {image && (
        <div className="imageContainer">
          <SPImage
            className={isProfile ? 'profileImage' : 'image'}
            src={image}
            width={88}
            height={88}
            alt="Choice image"
          />
        </div>
      )}
      {content && (
        <div
          className={`choiceText ${
            popupChoiceProp.status !== 'Active' && 'disableTextColor'
          }`}
        >
          {content}
        </div>
      )}
      {keys && (
        <div className="propList">
          {keys?.map((item: string) => {
            return (
              <div className="prop" key={item}>
                <div className="propTitle">{item}</div>
                <div className="propPoint">
                  {getSignedNumberString(Number(displays?.[item]) || 0)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ChoicePopup = ({
  onBoarding,
  isLastPopup,
  onNext,
}: {
  onBoarding: OnboardingPopup;
  isLastPopup: boolean;
  onNext: (
    mProps?: Record<string, number | string>,
    profileInfo?: CustomProfile,
  ) => void;
}) => {
  const { t } = useTranslation();
  const [choiceId, setChoiceId] = useState<number>();
  const [mProps, setProps] = useState<Record<string, number | string>>();
  const [isShowFloating, setIsShowFloating] = useState<boolean>(false);
  const [isEnableNext, setIsEnableNext] = useState<boolean>(false);
  const [isEnablePrev, setIsEnablePrev] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<{
    imageFileId?: number;
    imageFile?: {
      link: string;
    };
  }>();
  const playerClassIdRef = useRef<number>();

  const setEnableFloating = (currentScroll: HTMLDivElement) => {
    setIsEnableNext(
      Math.round(currentScroll.scrollLeft) + currentScroll.clientWidth <
        currentScroll.scrollWidth,
    );
    setIsEnablePrev(Math.round(currentScroll.scrollLeft) > 0);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.addEventListener('scroll', handleScroll);
      setIsShowFloating(
        scrollRef.current.clientWidth < scrollRef.current.scrollWidth,
      );
      setEnableFloating(scrollRef.current);
    }

    return window.removeEventListener('scroll', handleScroll);
  }, [onBoarding]);

  const handleScroll = throttle(() => {
    if (scrollRef.current) {
      setEnableFloating(scrollRef.current);
    }
  }, 300);

  const onChecked = ({
    popupChoiceId,
    props,
    imageFileId,
    imageFile,
    playerClassId,
  }: {
    popupChoiceId: number;
    props?: Record<string, number | string>;
    imageFileId?: number;
    imageFile?: {
      link: string;
    };
    playerClassId?: number;
  }) => {
    setChoiceId(popupChoiceId);
    setProps(props);

    imageRef.current = {
      imageFileId,
      imageFile,
    };
    playerClassIdRef.current = playerClassId;
  };

  const onPressNext = () => {
    const profileInfo: CustomProfile = {
      popupId: onBoarding.popupId,
      isMainCharacter: onBoarding.type === 'Profile',
      characterId: onBoarding?.chapterId || onBoarding.character?.chrId,
      profileImageFileId: imageRef.current?.imageFileId,
      profileImageFile: imageRef.current?.imageFile,
      playerClassId: playerClassIdRef.current,
    };
    onNext(mProps, profileInfo);
    setChoiceId(undefined);
    scrollRef.current?.scrollTo({
      left: 0,
      top: 0,
    });
  };

  const onScrollNext = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += scrollRef.current.clientWidth;
    }
  };
  const onScrollPrev = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft -= scrollRef.current.clientWidth;
    }
  };

  return (
    <div css={popupContainer}>
      <div className="title">{onBoarding?.description || ''}</div>
      <div className="choice-container">
        {isShowFloating && (
          <>
            {isEnablePrev ? (
              <FloatingPrevBtn
                className="arrow-icon prev"
                onClick={onScrollPrev}
                width={32}
                height={32}
              />
            ) : (
              <FloatingPrevDisableBtn
                className="arrow-icon prev"
                width={32}
                height={32}
              />
            )}
          </>
        )}
        <div ref={scrollRef} className="choiceList">
          {onBoarding?.popupChoices?.map((item: OnboardingPopupChoice) => (
            <ChoiceItem
              key={item.popupChoiceId}
              popupChoiceProp={item}
              onCheck={onChecked}
              isChecked={choiceId === item.popupChoiceId}
              isProfile={
                onBoarding.type === 'OtherProfile' ||
                onBoarding.type === 'Profile'
              }
            />
          ))}
        </div>
        {isShowFloating && (
          <>
            {isEnableNext ? (
              <FloatingNextBtn
                className="arrow-icon next"
                onClick={onScrollNext}
                width={32}
                height={32}
              />
            ) : (
              <FloatingNextDisableBtn
                className="arrow-icon next"
                width={32}
                height={32}
              />
            )}
          </>
        )}
      </div>
      <Button
        title={
          isLastPopup
            ? t('common_screen_button_start')
            : t('common_screen_label_next')
        }
        isDisable={!choiceId}
        onClick={onPressNext}
        customCSS={buttonStyle}
      />
    </div>
  );
};

const popupContainer = css`
  width: 100%;
  position: relative;
  border: none;
  border-radius: 0.75em;
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0rem 1rem 1rem 1rem;

  .title {
    min-width: 90%;
    ${theme.fontV3.body.body01Bold};
    text-align: center;
    color: var(--blackA85, rgba(0, 0, 0, 0.85));
    margin-bottom: 1.25rem;

    @media screen and (max-width: ${theme.devices.mobile}) {
      margin-bottom: 1em;
    }
  }

  .hintContainer {
    margin: 1em 0;
  }

  .hint {
    color: ${theme.colors.gray600};
    min-width: 90%;
    font-size: 0.87em;
    text-align: center;
    line-height: 1.25em;
  }

  .profile {
    display: flex;
    justify-content: center;
  }

  .choice-container {
    position: relative;
    max-width: 100%;
    display: flex;
  }

  .choiceList {
    max-width: 100%;
    display: flex;
    overflow-x: auto;
    margin-left: 2rem;
    margin-right: 2rem;
    gap: 0.5rem;
    scroll-behavior: smooth;

    ::-webkit-scrollbar {
      display: none;
    }
  }

  .arrow-icon {
    position: absolute;
    top: 40%;
    z-index: 1;
    filter: drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.08));
    cursor: pointer;
  }

  .prev {
    left: -0.5rem;
  }

  .next {
    right: -0.5rem;
  }

  @media (max-width: 768px) {
    padding: 2em 1.5em;

    .text {
      font-size: 1.2em;
    }
  }
`;

const choiceProp = css`
  min-width: 8.625rem;
  min-height: 8.625rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 0.75rem;
  border: 1px solid var(--blackA10, rgba(0, 0, 0, 0.1));
  padding: 1.25rem 1rem 1.25rem 1rem;
  cursor: pointer;

  .choiceText {
    text-align: center;
    max-width: 6em;
    min-height: 3rem;
    margin-top: 0.75rem;
    ${theme.fontV3.body.body03Bold};
    color: ${theme.colors.gray900};
    word-break: break-word;
  }

  .disableTextColor {
    color: ${theme.colors.gray500};
  }

  .imageContainer {
    margin: 0;
  }

  .image {
    width: 5.5rem;
    height: 5.5rem;
    border-radius: 0.25em;
  }

  .profileImage {
    width: 5.5rem;
    height: 5.5rem;
    border-radius: 50%;
  }

  .propList {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.75rem;
  }

  .prop {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin: 0 1em;
  }

  .propTitle {
    ${theme.fontV3.body.body05Reg};
    color: var(--blackA75, var(--transparent-black75, rgba(0, 0, 0, 0.75)));
  }

  .propPoint {
    ${theme.fontV3.body.body05Reg};
    color: ${theme.colors.primary500};
    width: 100%;
    max-width: 2.1875rem;
    text-align: right;
  }
`;

const selected = css`
  border: 1.5px solid ${theme.colors.primary500};
`;

const buttonStyle = css`
  width: 100%;
  height: 3.25rem;
  cursor: pointer;
  ${theme.fontV3.body.body03Bold};
  margin-top: 1.5rem;

  @media screen and (max-width: ${theme.devices.mobile}) {
    margin-top: 1.3em;
  }
`;

export default ChoicePopup;
